import { useState } from "react";
import { Award, Check, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/academyFormat";
import { ACADEMY } from "@/constants/testIds";

const brand = "bg-[#037782] hover:bg-[#025E67] text-white";

// Fix Y (2026-10-13): en la pagina del alumno, si no asistio: responder dentro
// del plazo (pedir otra fecha o contar que paso).
export function NoShowBox({ token, data, onChanged }) {
  const ns = data.no_show;
  const [action, setAction] = useState(ns?.other_dates?.length ? "reschedule" : "explain");
  const [edition, setEdition] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  if (!ns) return null;
  const send = async () => {
    setError("");
    setSaving(true);
    try {
      const { data: fresh } = await api.post(`/public/academy/enrollments/${token}/no-show`, { action, edition_id: action === "reschedule" ? edition : null, message });
      onChanged(fresh);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="bg-[#FBF1F1] border border-[#E8C9C8] rounded-2xl p-5 sm:p-6 space-y-3" data-testid={ACADEMY.portalNoShow}>
      <h2 className="font-serif text-xl text-[#1F2B2C]">No te vimos en el curso</h2>
      {ns.response ? (
        <p className="text-sm text-[#5B7A54] inline-flex items-start gap-1" data-testid={ACADEMY.portalNoShowAnswered}>
          <Check className="w-4 h-4 mt-0.5" /> Recibimos tu respuesta{ns.response.action === "reschedule" ? ` (pediste pasar al ${ns.response.edition_text})` : ""}. Te escribimos para confirmarte.
        </p>
      ) : (
        <>
          <p className="text-sm text-[#1F2B2C]">Tenés hasta el <strong>{fmtDateTime(ns.deadline)}</strong> para respondernos. Si no respondés, la reserva vence.</p>
          <div className="space-y-2 text-sm">
            {ns.other_dates.length > 0 && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" name="ns" className="mt-1" checked={action === "reschedule"} onChange={() => setAction("reschedule")} data-testid={ACADEMY.portalNoShowReschedule} />
                <span className="flex-1">Quiero pasar a otra fecha
                  {action === "reschedule" && (
                    <select className="mt-2 w-full h-10 rounded-md border border-[#D5E5E5] bg-white px-3 text-sm" value={edition} onChange={(e) => setEdition(e.target.value)} data-testid={ACADEMY.portalNoShowDateSelect}>
                      <option value="">Elegí la fecha</option>
                      {ns.other_dates.map((d) => <option key={d.id} value={d.id}>{d.when}{d.location ? ` · ${d.location}` : ""}</option>)}
                    </select>
                  )}
                </span>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="ns" checked={action === "explain"} onChange={() => setAction("explain")} data-testid={ACADEMY.portalNoShowExplain} />
              Quiero contar qué pasó
            </label>
          </div>
          <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={action === "explain" ? "Contanos qué pasó" : "Si querés, dejanos un comentario"} data-testid={ACADEMY.portalNoShowMessage} />
          {error && <p className="text-sm text-destructive" data-testid={ACADEMY.portalNoShowError}>{error}</p>}
          <Button className={brand} onClick={send} disabled={saving} data-testid={ACADEMY.portalNoShowSubmit}>{saving ? "Enviando..." : "Enviar respuesta"}</Button>
        </>
      )}
    </section>
  );
}

// Despues del curso: practicas y diploma.
export function DiplomaBox({ token, data }) {
  const dip = data.diploma;
  if (!dip) return null;
  return (
    <section className="bg-white border border-[#D5E5E5] rounded-2xl p-5 sm:p-6 space-y-3" data-testid={ACADEMY.portalDiplomaSection}>
      <div className="flex items-center gap-2">
        <Award className="w-5 h-5 text-[#199095]" />
        <h2 className="font-serif text-xl text-[#1F2B2C]">Tu diploma</h2>
      </div>
      {data.practices.required > 0 && (
        <p className="text-sm text-[#1F2B2C]" data-testid={ACADEMY.portalPractices}>Prácticas: <strong>{data.practices.done} de {data.practices.required}</strong></p>
      )}
      {/* Fix D (2026-10-18): las practicas que tiene agendadas */}
      {(data.next_practices || []).length > 0 && (
        <p className="text-sm text-[#1F2B2C]" data-testid={ACADEMY.portalNextPractices}>
          Próximas prácticas: <strong>{data.next_practices.map((p) => `${fmtDate(p.date)}${p.time ? ` a las ${p.time}` : ""}`).join(" · ")}</strong>
        </p>
      )}
      {dip.ready ? (
        <a href={`${API}/public/academy/enrollments/${token}/diploma`} className={`inline-flex items-center gap-2 h-10 px-4 rounded-md text-sm ${brand}`} data-testid={ACADEMY.portalDiplomaDownload}><Download className="w-4 h-4" /> Descargar diploma</a>
      ) : (
        <div className="text-sm text-[#566768]" data-testid={ACADEMY.portalDiplomaMissing}>
          Para recibir tu diploma falta:
          <ul className="list-disc pl-5 mt-1">{dip.missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
    </section>
  );
}
